import { useLayoutEffect, useState } from 'react';

/**
 * Wie viele Chips einer umbrechenden Zeile in zwei Reihen passen — gemessen an einem
 * unsichtbaren Mess-Container, der ALLE Chips in der Zielbreite rendert (Muster aus
 * ui/FilterStrip: messen, dann entscheiden). Reihen erkennt der Hook an unterschiedlichen
 * `offsetTop`-Werten der Kinder mit der Klasse `badge`.
 *
 * Rückgabe: `Infinity`, wenn alle Chips in zwei Reihen stehen; sonst die Zahl der Chips vor
 * dem ersten Chip der dritten Reihe MINUS eins — der frei werdende Platz ist für den
 * „+N"-Chip. `fallback`, solange kein Layout vorliegt (alle `offsetTop` gleich 0 bei mehr
 * Chips als `fallback`, so in jsdom).
 */
export function useTwoRowChipLimit(measureRef, itemCount, fallback) {
  const [limit, setLimit] = useState(fallback);

  useLayoutEffect(() => {
    const container = measureRef.current;
    if (!container) return undefined;

    const messen = () => {
      const chips = Array.from(container.querySelectorAll('.badge'));
      if (chips.length === 0) { setLimit(Infinity); return; }
      const tops = chips.map((chip) => chip.offsetTop);
      const reihen = [...new Set(tops)].sort((a, b) => a - b);
      const keinLayout = reihen.length === 1 && reihen[0] === 0 && chips.length > fallback;
      if (keinLayout) { setLimit(fallback); return; }
      if (reihen.length <= 2) { setLimit(Infinity); return; }
      const ersterInDritterReihe = tops.findIndex((top) => top >= reihen[2]);
      setLimit(Math.max(1, ersterInDritterReihe - 1));
    };

    messen();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const beobachter = new ResizeObserver(messen);
    beobachter.observe(container);
    return () => beobachter.disconnect();
  }, [measureRef, itemCount, fallback]);

  return limit;
}
